import { type RecordId } from '../types';
import Debug from 'debug';
import type WhiteboardController from './WhiteboardController';
import ToolPaste from '../tools/ToolPaste';
import { ToolFitToScreen } from '../tools/ToolFitToScreen';
import { type IToolActionInput } from '../tools/Tool';
import ToolDelete from '../tools/ToolDelete';
import { observable } from '@aha-app/mvc';

const debug = Debug('whiteboard');

interface HistoryManagerState {
  whiteboardVersions: Aha.WhiteboardVersion[] | null;
  previewVersionId: RecordId | null;
  previewData: any;
  fetchingPreview: boolean;
  flashLatestVersion: boolean;
}

export interface HistoryDataManager {
  fetchWhiteboardVersions: (
    whiteboardId: RecordId
  ) => Promise<Aha.WhiteboardVersion[]>;
  fetchWhiteboardVersion: (
    whiteboardId: RecordId,
    whiteboardVersionId: RecordId
  ) => Promise<Aha.WhiteboardVersion>;
  createVersionSnapshot: (whiteboardId: RecordId) => Promise<Response>;
}

export default class HistoryManager {
  public state: HistoryManagerState;

  constructor(
    private readonly whiteboardController: WhiteboardController,
    private readonly whiteboardId: RecordId,
    private readonly historyDataManager: HistoryDataManager
  ) {
    this.state = observable({
      whiteboardVersions: null,
      previewVersionId: null,
      previewData: null,
      fetchingPreview: false,
      flashLatestVersion: false,
    });
  }

  allowedToRevert() {
    return (
      !this.whiteboardController.isReadOnly() &&
      this.whiteboardController.props.allowUpdate
    );
  }

  async actionGetWhiteboardVersions() {
    this.state.flashLatestVersion = false;

    const whiteboardVersions =
      await this.historyDataManager.fetchWhiteboardVersions(this.whiteboardId);
    this.state.whiteboardVersions = whiteboardVersions.sort(
      (a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
  }

  async actionSaveVersion() {
    debug('Creating a snapshot of the current version');

    // Start by emptying the whiteboardVersions so a loading indicator is shown
    this.state.whiteboardVersions = null;

    await this.actionCreateVersionSnapshot();

    // Reload the whiteboard versions so we pick up the newly created version
    await this.actionGetWhiteboardVersions();

    this.state.flashLatestVersion = true;
  }

  async actionRevertVersion(versionId: RecordId) {
    debug('Reverting to version', versionId);

    window.AhaAnalytics?.track(
      'Feature-7240252275556413975',
      'User restores whiteboard history',
      {}
    );

    this.actionClosePreview();

    const whiteboardVersion =
      await this.historyDataManager.fetchWhiteboardVersion(
        this.whiteboardId,
        versionId
      );

    const oldContent = whiteboardVersion.editorContent;

    const { whiteboardController } = this;

    // Start by emptying the whiteboardVersions so a loading indicator is shown
    this.state.whiteboardVersions = null;

    await this.actionCreateVersionSnapshot();

    // Bad things happen if we try and delete objects that are selected
    whiteboardController.canvas.discardActiveObject();

    // Delete all items on the whiteboard
    ToolDelete.deleteObjects(
      whiteboardController.canvas.getObjects(),
      whiteboardController
    );

    // Add all the items from the old content
    await ToolPaste.pasteContents(whiteboardController, oldContent, {
      x: 0,
      y: 0,
    });

    whiteboardController.canvas.requestRenderAll();

    // Tools expect a selection, but this tool doesn't use it. We should pull
    // the ToolFitToScreen.action guts into a separate function so we can call
    // it directly without type casting this param.
    ToolFitToScreen.action({
      controller: whiteboardController,
    } as IToolActionInput);

    // Reload the whiteboard versions so we pick up the newly created version
    await this.actionGetWhiteboardVersions();
  }

  async actionPreviewVersion(versionId: RecordId) {
    debug('Previewing version', versionId);

    window.AhaAnalytics?.track(
      'Feature-7240252275556413975',
      'User previews whiteboard history',
      {}
    );

    this.state.fetchingPreview = true;

    const whiteboardVersion =
      await this.historyDataManager.fetchWhiteboardVersion(
        this.whiteboardId,
        versionId
      );

    this.state.previewVersionId = versionId;
    this.state.previewData = whiteboardVersion.editorContent;
    this.state.fetchingPreview = false;
  }

  actionClosePreview() {
    this.state.previewVersionId = null;
    this.state.previewData = null;
  }

  private async actionCreateVersionSnapshot() {
    // Make a checkpoint of the current version. This will use whatever the
    // server has at the time, which is possibly slightly desynced from what
    // this client has. It will only be a few seconds worth of content at most,
    // so the difference is ok.
    // Call the endpoint POST /whiteboards/:whiteboardId/whiteboard_version using fetch
    // and make sure it is successful before proceeding
    const response = await this.historyDataManager.createVersionSnapshot(
      this.whiteboardId
    );

    if (!response.ok) {
      throw new Error(
        `Error creating whiteboard version snapshot! Status: ${response.status}`
      );
    }
  }
}
